import React from "react";
import { isEmpty } from "lodash";
import { FS } from "@plannel/utils/font-util";

/*
 * recharts 차트의 공용 툴팁 — **ag-charts 기본 툴팁과 같은 모양**.
 *
 * 왜 있나: 이 앱은 recharts 와 ag-charts(DP Workbench)를 같이 쓴다. 두 라이브러리의 기본 툴팁이
 * 달라서 화면을 옮겨 다니면 티가 난다. recharts 기본 툴팁은 심볼이 없고 **라벨을 시리즈 색으로
 * 칠하는데**, ag-charts 는 **색상 심볼 + 검은 라벨 + 우측 값**이다. contentStyle 로는 못 맞춘다
 * (행 구조가 달라서) → 그래서 content 를 직접 그린다.
 *
 * 아래 값은 ag-charts-community 13.3.1 번들의 실제 CSS 에서 가져왔다(추측 아님):
 *   --ag-charts-tooltip-background-color: #fafafa   --ag-charts-tooltip-text-color: #181d1f
 *   --ag-charts-border-color: #dddddd               --ag-charts-border-radius: 4px
 *   --ag-charts-tooltip-border-width: 1px           --ag-charts-popup-shadow: 0 0 16px rgba(0,0,0,.15)
 *   --ag-charts-chrome-font-*: Verdana, sans-serif / 12px / 400
 *   .ag-charts-tooltip-content { display:grid; grid:auto-flow minmax(1em,auto) / auto 1fr;
 *                                padding:8px 12px; gap:8px }
 *   .ag-charts-tooltip-heading { grid-column:1 / -1 }
 *   .ag-charts-tooltip-symbol  { grid-column:1 / 2; place-self:center }
 *   .ag-charts-tooltip-row     { grid-column:-2 / -1; display:flex; gap:16px;
 *                                align-items:baseline; justify-content:space-between }
 *   .ag-charts-tooltip-label   { flex:1; min-width:0 }
 * ag-charts 를 올릴 때 이 값들도 같이 확인할 것.
 *
 * 예외: 심볼 크기(8px)만 번들에서 못 뽑았다 — SVG 라 시리즈 marker 설정에서 온다. 화면 기준 근사치다.
 *
 * 쓰는 법 — <Tooltip> 의 formatter/contentStyle 은 그대로 두고 content 만 끼운다:
 *   <Tooltip formatter={fmt} content={<AgStyleTooltipContent />} />
 * recharts 가 <Tooltip> props 를 content 로 넘겨주므로(recharts/lib/component/Tooltip.js
 * renderContent → cloneElement(content, props)) formatter·contentStyle 이 여기로 들어온다.
 *
 * 행 구성(그 x 의 모든 시리즈를 한 번에)은 recharts 그대로다 — 맞춘 건 생김새뿐이다.
 *
 * ⚠ 툴팁 **내용**이 다른 차트(산점도, 파이 등 자체 CustomTooltip 을 쓰는 곳)는 대상이 아니다.
 *   여기는 "기본 툴팁의 생김새"를 통일하는 자리다.
 */
export const AG_TOOLTIP_CONTENT_STYLE = {
  display: "grid",
  gridTemplateColumns: "auto 1fr",
  gridAutoRows: "minmax(1em, auto)",
  gap: 8,
  padding: "8px 12px",
  backgroundColor: "#FFFDF7",
  border: "1px solid #EFE9DC",
  borderRadius: 6,
  boxShadow: "0 0 16px rgba(0, 0, 0, .15)",
  fontFamily: "Verdana, sans-serif",
  fontSize: FS.SUB,
  fontWeight: 400,
  color: "rgba(0, 0, 0, 0.82)",
  whiteSpace: "nowrap",
};
/*
 * ag-charts 의 compact 변형 — 좁은 자리(ag-grid 셀 안의 미니 차트 등)를 위해 라이브러리가 직접 두는 옵션.
 *   .ag-charts-tooltip--compact .ag-charts-tooltip-content {
 *     --tooltip-row-spacing: 2px; --tooltip-column-spacing: 8px; padding: 3px 6px }
 */
export const AG_TOOLTIP_COMPACT_CONTENT_STYLE = { gap: 2, padding: "3px 6px" };
const AG_TOOLTIP_COMPACT_ROW_STYLE = { gap: 8 };

export const AG_TOOLTIP_HEADING_STYLE = { gridColumn: "1 / -1" };
export const AG_TOOLTIP_SYMBOL_STYLE = { gridColumn: "1 / 2", placeSelf: "center", width: 8, height: 8 };
export const AG_TOOLTIP_ROW_STYLE = { gridColumn: "-2 / -1", display: "flex", gap: 16, alignItems: "baseline", justifyContent: "space-between" };
export const AG_TOOLTIP_LABEL_STYLE = { flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" };
export const AG_TOOLTIP_VALUE_STYLE = { minWidth: 0 };

/*
 * 툴팁 **내용**이 달라서 AgStyleTooltipContent 를 그대로 못 쓰는 차트(산점도처럼 한 점의 여러 축을
 * 나열하거나, 파이처럼 모든 조각을 항상 보여주는 경우)를 위한 조립용 부품.
 * 이걸로 감싸면 생김새는 자동으로 통일된다.
 *
 *   <AgTooltipBox>
 *     <AgTooltipRow color="#048BA8" label="Sales Qty" value="1,234" />
 *     <AgTooltipRow label="X" value="12" />        // color 생략 = 심볼 자리만 비움(정렬 유지)
 *   </AgTooltipBox>
 */
export const AgTooltipBox = ({ children, style, compact }) => (
  <div style={{ ...AG_TOOLTIP_CONTENT_STYLE, ...(compact ? AG_TOOLTIP_COMPACT_CONTENT_STYLE : {}), ...style }}>
    {children}
  </div>
);

export const AgTooltipRow = ({ color, label, value, compact }) => (
  <>
    {/* color 가 없으면 배경 없는 빈 칸 — 그리드 1열을 차지해 라벨 시작점을 맞춘다 */}
    <span style={color ? { ...AG_TOOLTIP_SYMBOL_STYLE, backgroundColor: color } : AG_TOOLTIP_SYMBOL_STYLE} />
    <div style={{ ...AG_TOOLTIP_ROW_STYLE, ...(compact ? AG_TOOLTIP_COMPACT_ROW_STYLE : {}) }}>
      <span style={AG_TOOLTIP_LABEL_STYLE}>{label}</span>
      <span style={AG_TOOLTIP_VALUE_STYLE}>{value}</span>
    </div>
  </>
);

/**
 * recharts <Tooltip content={...}> 용 컴포넌트.
 *
 * @param active       recharts 주입
 * @param payload      recharts 주입 — 그 x 의 시리즈 배열
 * @param label        recharts 주입 — x 값
 * @param formatter    <Tooltip formatter> 그대로. content 를 쓰면 recharts 가 자동 적용해 주지
 *                     않으므로 여기서 직접 부른다(안 부르면 통화기호·자릿수 포맷이 사라진다)
 * @param contentStyle <Tooltip contentStyle> 그대로. 호출측 값을 덮지 않고 얹는다
 */
export const AgStyleTooltipContent = ({ active, payload, label, formatter, contentStyle, compact }) => {
  if (!active || isEmpty(payload)) {
    return null;
  }

  /*
   * recharts 의 formatter 계약: 값만 반환할 수도 있고 **[값, 이름] 배열**을 반환할 수도 있다
   * (기본 툴팁이 그렇게 해석한다 — DefaultTooltipContent). 배열을 그대로 렌더하면 React 가
   * 두 원소를 이어 붙여 "93.5utilization" 처럼 나온다. 실제로 ResourceUtilChart 가 배열을 준다.
   */
  const formatEntry = (entry, index) => {
    if (!formatter) {
      return { name: entry.name, value: entry.value };
    }
    const formatted = formatter(entry.value, entry.name, entry, index, payload);
    return Array.isArray(formatted)
      ? { value: formatted[0], name: formatted[1] ?? entry.name }
      : { value: formatted, name: entry.name };
  };

  return (
    <AgTooltipBox style={contentStyle} compact={compact}>
      {/* 파이 차트처럼 x 축이 없는 차트는 label 이 비어 온다 — 빈 제목 줄을 만들지 않는다 */}
      {isEmpty(String(label ?? "")) ? null : <div style={AG_TOOLTIP_HEADING_STYLE}>{label}</div>}
      {payload.map((entry, index) => {
        const { name, value } = formatEntry(entry, index);
        return (
          <AgTooltipRow
            key={`${entry.dataKey}-${index}`}
            // 시리즈 색: Bar/Line/Area 는 color, Pie 는 조각의 payload.fill 에 들어온다
            color={entry.color ?? entry.fill ?? entry.stroke ?? entry.payload?.fill}
            label={name}
            value={value}
            compact={compact}
          />
        );
      })}
    </AgTooltipBox>
  );
};

export default AgStyleTooltipContent;
